import { useCallback, useEffect, useRef, useState } from 'react'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'

import { CoreComponentFormProps, CoreComponentInfos } from '@/core/models/app-components/types'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useParseVariableSelect } from '@/hooks/__project__/useParseVariableSelect'
import { useParseVariableText } from '@/hooks/__project__/useParseVariableText'
import { ComponentSettingKey } from '@/shared/builder'
import { parseSpacing } from '@/shared/builder-utils'
import { SpacingInputValue } from '@/shared/builder/types/spacing-input'
import { FORM_VALIDATION_PATTERNS, formatColumnValue, validatePattern } from '@/shared/form-utils'
import { getSafely } from '@/shared/safely'
import { isEmpty } from '@/shared/utils'
import { useUpdateFormDefaultValue } from '@/components/__project__/hooks/useUpdateFormDefaultValue'
import { CompOverlayDevmode } from '@/components/__project__/shared/CompOverlayDevmode'

import { CompTextFieldProps } from './comp'

function getInputType(columnType: ColumnTypes) {
  switch (columnType) {
    case ColumnTypes.RichText:
      return 'textarea'
    case ColumnTypes.Email:
      return 'email'
    case ColumnTypes.Phone:
      return 'tel'
    case ColumnTypes.URL:
      return 'url'
    case ColumnTypes.Number:
      return 'number'
    default:
      return 'text'
  }
}

export interface CompTextFieldWrapperProps {
  infos: CoreComponentInfos
  form: CoreComponentFormProps
  children: (props: CompTextFieldProps, ref: React.RefObject<HTMLInputElement>) => React.ReactNode
}

/**
 * CompTextFieldWrapperCore
 */
export function CompTextFieldWrapperCore({
  infos: { settings, containerClient, detailTableId, detailRowIndex, component },
  form,
  children,
}: CompTextFieldWrapperProps) {
  const { openComponentSettings, isDevelopmentMode } = useAppBuilderContext()

  const handleClick = () => {
    openComponentSettings(component)
    if (isDevelopmentMode) return
  }

  // ========================= form settings =========================
  const { dataSourceTable, formState, setFormState, formErrors, setFormErrors, setRequiredFieldStatus } = form

  // ========================= data =========================
  const columnId = settings[ComponentSettingKey.TextFieldDataValue] as string
  const column = dataSourceTable?.columnsMap?.get(columnId)

  const defaultValue = useParseVariableSelect<string>(
    settings[ComponentSettingKey.TextFieldDataDefaultValue],
    containerClient,
    detailTableId,
    detailRowIndex
  )

  const isEditable = getSafely(settings, [ComponentSettingKey.TextFieldDataDefaultValueEditable], true) as boolean

  const supportingTextData = settings?.[ComponentSettingKey.TextFieldDataHelp]
  const supportingText = useParseVariableText(supportingTextData, containerClient, detailTableId, detailRowIndex)
  const labelData = settings?.[ComponentSettingKey.TextFieldDataLabel]
  const label = useParseVariableText(labelData, containerClient, detailTableId, detailRowIndex)
  const leadingIcon = settings?.[ComponentSettingKey.TextFieldDataLeadingIcon] as string

  const prefixData = settings?.[ComponentSettingKey.TextFieldDataPrefix]
  const prefix = useParseVariableText(prefixData, containerClient, detailTableId, detailRowIndex)
  const suffixData = settings?.[ComponentSettingKey.TextFieldDataSuffix]
  const suffix = useParseVariableText(suffixData, containerClient, detailTableId, detailRowIndex)

  // ========================= design =========================

  const height = settings?.[ComponentSettingKey.TextFieldDesignHeight] || 'small'
  const isTextArea = height !== 'small'

  // large: rows = 5
  // medium: rows = 3
  // small: rows = 1
  const rows = height === 'large' ? 6 : height === 'medium' ? 4 : 1

  const padding = parseSpacing(settings?.[ComponentSettingKey.TextFieldDesignMargin] as SpacingInputValue)

  const min = (settings?.[ComponentSettingKey.TextFieldBehaviorMinChar] as number) ?? 1
  const max = (settings?.[ComponentSettingKey.TextFieldBehaviorMaxChar] as number) ?? 1000

  // ========================= behavior =========================
  const pattern = getSafely(settings, [ComponentSettingKey.TextFieldBehaviorPattern], 'none') as string
  const showValidationHelpText = getSafely(
    settings,
    [ComponentSettingKey.TextFieldBehaviorPatternShowHelp],
    true
  ) as boolean
  const defaultHelpText = showValidationHelpText
    ? FORM_VALIDATION_PATTERNS.find((p) => p.pattern === pattern)?.helpText || ''
    : ''

  const passwordMask = settings?.[ComponentSettingKey.TextFieldBehaviorPasswordMask] as boolean
  const required = settings?.[ComponentSettingKey.TextFieldBehaviorRequired] as boolean
  const showCharCounter = settings?.[ComponentSettingKey.TextFieldBehaviorCharacterCounter] as boolean

  // ========================= form state =========================

  // ========================= form state =========================
  const value = formState[columnId] as string | number | undefined
  const error = formErrors?.[columnId]

  const [debouncedValue, setDebouncedValue] = useState(value)
  const [isTouched, setIsTouched] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleUpdateDefaultValue = useCallback(
    (defValue: string | number | undefined) => {
      if (!columnId) return

      const newValue = formatColumnValue(defValue, column)
      if (inputRef.current) {
        inputRef.current.value = newValue
      }
      setFormState((prev) => ({ ...prev, [columnId]: newValue }))
    },
    [columnId, column, setFormState]
  )

  useUpdateFormDefaultValue({
    defaultValue: formatColumnValue(defaultValue, column),
    value: formState[columnId],
    onUpdate: handleUpdateDefaultValue,
  })

  // Update handleChange
  const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
    if (!columnId) return

    if (!isTouched) {
      setIsTouched(true)
    }

    const val = e.currentTarget.value
    const newValue = val.slice(0, max)

    // Update form state immediately
    setFormState((prev) => ({ ...prev, [columnId]: formatColumnValue(newValue, column) }))

    if (val.length > max && inputRef.current) {
      inputRef.current.value = newValue
    }
  }

  // Handle clear action
  const handleClear = () => {
    if (!columnId) return

    if (!isTouched) {
      setIsTouched(true)
    }

    setFormState((prev) => ({ ...prev, [columnId]: null }))
    setFormErrors((prev) => {
      delete prev[columnId]
      return prev
    })

    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const validateField = useCallback(
    (val: string | number | undefined) => {
      if (!columnId) return

      if (isEmpty(val)) {
        return
      }
      let valueToValidate = val?.toString() || ''

      if (min && valueToValidate.length < min) {
        setFormErrors((prev) => ({ ...prev, [columnId]: `Enter at least ${min} characters.` }))
        return
      }

      if (max && valueToValidate.length > max) {
        setFormErrors((prev) => ({ ...prev, [columnId]: `Maximum characters is ${max}` }))
        return
      }

      if (!!valueToValidate && pattern !== 'none') {
        validatePattern(pattern, valueToValidate, (isValid, error) => {
          if (isValid) {
            setFormErrors((prev) => {
              const newErrors = { ...prev }
              delete newErrors[columnId]
              return newErrors
            })
          } else {
            setFormErrors((prev) => ({ ...prev, [columnId]: error }))
          }
        })
      } else {
        setFormErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[columnId]
          return newErrors
        })
      }
    },
    [columnId, max, min, pattern, setFormErrors]
  )

  // Use effect for validation
  useEffect(() => {
    validateField(debouncedValue)
  }, [debouncedValue, validateField])

  // Cleanup timeout on unmount
  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null

    // Clear any existing timeout
    if (timeout) {
      clearTimeout(timeout)
    }

    // Set a new timeout for validation
    timeout = setTimeout(() => {
      setDebouncedValue(value)
    }, 500)

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [value])

  // Add effect to update required field status
  useEffect(() => {
    if (required) {
      setRequiredFieldStatus(columnId, isEmpty(value))
    }
    return () => {
      if (required) {
        setRequiredFieldStatus(columnId, false)
      }
    }
  }, [required, value, columnId, setRequiredFieldStatus])

  // ========================= render =========================

  const textFieldProps: CompTextFieldProps = {
    value: value ?? '',
    onChange: handleChange,
    onClear: handleClear,
    label,
    type: passwordMask ? 'password' : getInputType(column?._Type as ColumnTypes),
    required,
    disabled: !isEditable,
    error,
    supportingText: supportingText || defaultHelpText,
    leadingIcon,
    prefix,
    suffix,
    charCounter: showCharCounter,
    maxCharCount: max,
    isTextArea,
    rows,
  }

  if (isDevelopmentMode) {
    return (
      <div className='relative inline-block w-full border border-dashed' style={{ borderColor: '#333' }}>
        {children(textFieldProps, inputRef)}
        <CompOverlayDevmode handleClick={handleClick} />
      </div>
    )
  }

  return children(textFieldProps, inputRef)
}
